import React from 'react';
import { X, Calendar, User, Search, Download } from 'lucide-react';
import { createPortal } from 'react-dom';
import { RateioCredito, RateioCreditoItem } from '../../../types';
import { propsFundoModal } from '../../../src/lib/backdropModal';

interface RateioManageModalProps {
  rateio: RateioCredito;
  itens: RateioCreditoItem[];
  onClose: () => void;
}

const RateioManageModal: React.FC<RateioManageModalProps> = ({ rateio, itens, onClose }) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  const formatDayValue = (val: any) => {
    if (!val || val === '-') return '-';

    const num = Number(val);
    // Se for um número grande (ex: 46086), converte de serial do Excel para o dia
    if (!isNaN(num) && num > 31) {
      const date = new Date(Math.round((num - 25569) * 86400 * 1000));
      if (!isNaN(date.getTime())) return String(date.getUTCDate());
    }

    return val;
  };

  const filteredItens = itens.filter(item =>
    (item.uc?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.nome?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.cpf_cnpj?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in custom-scrollbar" {...propsFundoModal(() => onClose())}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white font-poppins">
              Gerenciar Rateio de Créditos
            </h2>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1"><Calendar size={14} /> Mês Ref: {rateio.mes_referencia}</span>
              <span className="flex items-center gap-1"><User size={14} /> Criado por: {rateio.usuario_nome}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 bg-gray-50/50 dark:bg-gray-900/20">
          <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="Buscar por UC, Nome, CPF/CNPJ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white sm:text-sm"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Mostrando {filteredItens.length} ítens
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">UC</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Novo N° UC</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome do Cliente</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">CPF/CNPJ</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">UC Geradora</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Apresentação</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vencimento</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Data Leit. Ant.</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredItens.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      Nenhum item encontrado para sua busca.
                    </td>
                  </tr>
                ) : (
                  filteredItens.map((item, index) => (
                    <tr key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium">{item.uc || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item.outro_numero_uc || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={item.nome}>{item.nome || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item.cpf_cnpj || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {item.status ? (
                           <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                            ${item.status.toUpperCase() === 'ATIVA' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                              item.status.toUpperCase() === 'CANCELADA' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`
                           }>
                             {item.status}
                           </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{item.uc_geradora || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatDayValue(item.dia_apresentacao)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatDayValue(item.dia_vencimento)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatDayValue(item.dia_leitura_anterior)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RateioManageModal;
